import { useMemo } from 'react';

/**
 * useGraphDeclaredVariables — coleta todas as variáveis declaradas no grafo.
 *
 * Varre os nodes do React Flow e agrupa variáveis de três fontes:
 *
 *   - extract_variables (LLM extraction, nós ai_response e capability)
 *   - set_variable (action que define variável fixa)
 *   - collect_field (action que coleta dado do cliente, com sync_crm)
 *
 * Retorna lista [{ name, type, source, sourceNodeId, sourceNodeLabel, options? }].
 * Tipos são preservados quando declarados; collect_field e set_variable são
 * tratados como 'string' (pode ser refinado no futuro).
 *
 * Memoizado por um hash dos configs relevantes — atualiza apenas quando uma
 * declaração efetiva muda, não a cada re-render do canvas.
 */
const useGraphDeclaredVariables = (nodes = []) => {
  const fingerprint = useMemo(() => {
    return (nodes || [])
      .map((n) => {
        const data = n?.data || {};
        const config = data.config || {};
        // Apenas campos relevantes — evita refrescos por mudança em pos/handles.
        return [
          n?.id,
          data.key,
          data.label,
          JSON.stringify(config.extract_variables || []),
          config.variableName || config.variable_name || '',
          config.field_name || '',
        ].join('|');
      })
      .join('||');
  }, [nodes]);

  return useMemo(() => {
    const declared = [];
    const seen = new Set();

    const push = (entry) => {
      if (!entry?.name) return;
      const key = `${entry.name}::${entry.source}::${entry.sourceNodeId}`;
      if (seen.has(key)) return;
      seen.add(key);
      declared.push(entry);
    };

    (nodes || []).forEach((node) => {
      const data = node?.data || {};
      const config = data.config || {};
      const label = data.label || data.key || node?.id || 'Nó';

      // 1) extract_variables (LLM extraction)
      const extracts = Array.isArray(config.extract_variables) ? config.extract_variables : [];
      extracts.forEach((v) => {
        if (!v?.name) return;
        push({
          name: v.name,
          type: v.type || 'string',
          source: 'extract',
          sourceNodeId: node.id,
          sourceNodeLabel: label,
          options: Array.isArray(v.options) ? v.options : undefined,
          description: v.description || '',
        });
      });

      // 2) set_variable (campo varia por convenção — tentamos os comuns)
      if (data.key === 'set_variable') {
        const name = config.variableName || config.variable_name || config.name || '';
        if (name) {
          push({
            name,
            type: 'string',
            source: 'set_variable',
            sourceNodeId: node.id,
            sourceNodeLabel: label,
          });
        }
      }

      // 3) collect_field (campo de coleta com sync_crm)
      if (data.key === 'collect_field') {
        const name = config.field_name || '';
        if (name) {
          push({
            name,
            type: 'string',
            source: 'collect_field',
            sourceNodeId: node.id,
            sourceNodeLabel: label,
          });
        }
      }
    });

    return declared;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);
};

export default useGraphDeclaredVariables;
